import React, { useState, useEffect } from "react";
import DGHelperUsersTable from "./DGHelperUsersTable";
import {
  AddUserDialog,
  EditUserDialog,
  ManagePermissionsDialog,
} from "./DGHelperUsersDialogs";
import {
  fetchUsers,
  fetchRoles,
  createUser,
  updateUser,
  deleteUser,
  changeUserRole,
} from "./DGHelperUsersAPI";
import PageLayout from "../../components/PageLayout";
import SearchToolbar from "../../components/SearchToolbar";
import usePageTitle from "../../services/usePageTitle";
import usePageState from "../../hooks/usePageState";
import useIsRoleUser from "../../hooks/useIsRoleUser";
import { useTheme } from "../../services/useTheme";
import { extractMsg } from "../../utils/errorUtils";
import dghelperUsersHelp from "../../content/help/dghelper-users.json";

export default function DGHelperUsers({ pageTitle = "DGHelper Users" }) {
  usePageTitle("DGHelper Users - SkeyeGate™");
  const isDarkTheme = useTheme();

  const {
    filteredData,
    loading,
    searchText,
    page,
    pageSize,
    messageApi,
    contextHolder,
    handleSearch,
    clearFilter,
    loadData,
    onPageChange,
  } = usePageState({
    fetchFn: fetchUsers,
    filterFields: ["name", "username", "email"],
  });

  const [rolesList, setRolesList] = useState([]);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [permissionsModalOpen, setPermissionsModalOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const isRoleUser = useIsRoleUser();

  useEffect(() => {
    fetchRoles()
      .then(setRolesList)
      .catch((err) => console.error("Failed to fetch roles:", err));
  }, []);

  const handleAddUser = async (values) => {
    try {
      await createUser(values);
      messageApi.success("User created successfully");
      loadData();
    } catch (error) {
      throw error;
    }
  };

  const handleEditUser = async (values) => {
    try {
      const updatePayload = { email: values.email };
      if (values.password) updatePayload.password = values.password;
      await updateUser(userToEdit.id, updatePayload);

      messageApi.success("User updated successfully");
      loadData();
    } catch (error) {
      throw error;
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await deleteUser(userId);
      messageApi.success("User deleted successfully.");
      loadData();
    } catch (error) {
      messageApi.error({
        content: extractMsg(error, "Failed to delete user"),
        key: "delete-user-error",
      });
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      await changeUserRole(userId, newRole);
      messageApi.success("Role updated successfully.");
      loadData();
    } catch (error) {
      messageApi.error({
        content: extractMsg(error, "Failed to update role"),
        key: "change-role-error",
      });
    }
  };

  const logoSrc = `/img/DGHELPER_nobg${isDarkTheme ? "_white" : ""}.png`;

  return (
    <div className="dghelper-users-page">
      {contextHolder}
      <PageLayout
        title={pageTitle}
        logoSrc={logoSrc}
        description="Manager for DGHelper users, allowing you to create, edit, and organize your user accounts and permissions in one place."
        helpContent={dghelperUsersHelp}
      >
        <SearchToolbar
          search={searchText}
          onSearchChange={handleSearch}
          onClearFilter={clearFilter}
          addButtonLabel="Add User"
          onAdd={() => setAddModalOpen(true)}
          showAdd={!isRoleUser}
        />

        <DGHelperUsersTable
          data={filteredData}
          loading={loading}
          page={page}
          pageSize={pageSize}
          onPageChange={onPageChange}
          rolesList={rolesList}
          isRoleUser={isRoleUser}
          onChangeRole={handleChangeRole}
          onEditUser={(user) => {
            setUserToEdit(user);
            setEditModalOpen(true);
          }}
          onDeleteUser={handleDeleteUser}
          onManagePermissions={(user) => {
            setUserToEdit(user);
            setPermissionsModalOpen(true);
          }}
        />
      </PageLayout>

      <AddUserDialog
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSave={handleAddUser}
        rolesList={rolesList}
      />
      <EditUserDialog
        open={editModalOpen}
        user={userToEdit}
        onClose={() => setEditModalOpen(false)}
        onSave={handleEditUser}
        rolesList={rolesList}
      />
      <ManagePermissionsDialog
        open={permissionsModalOpen}
        user={userToEdit}
        onClose={() => setPermissionsModalOpen(false)}
        onSuccess={loadData}
        messageApi={messageApi}
      />
    </div>
  );
}
